import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import {
    Row, Col, Card, Form,
    FormGroup, Label, Input, Button
} from "reactstrap";
import withRouter from "../../components/Common/withRouter";
import { getOrganizations } from "../../store/actions/organization";
import whitelog from "../../assets/images/transparent.png";
import { registerUser } from "../../store/actions/user";
import { closeProgressDialog, showProgressDialog, showSuccessDialog } from "../../utils/dialogs";
import Select from "react-select";
import { MdEmail } from "react-icons/md";
import { FiUser, FiMail, FiLock, FiBriefcase, FiCheck } from "react-icons/fi";

const Register = (props) => {
    document.title = "Register | M-SPHERE";
    const dispatch = useDispatch();
    const organizations = useSelector((state) => state.organizations.activeOrganizations);

    const [formData, setFormData] = useState({
        firstName: "",
        middleName: "",
        lastName: "",
        email: "",
        password: "",
        confirmPassword: "",
        organization: null,
        agreeTerms: false
    });

    const [errors, setErrors] = useState({});
    const [organizationOptions, setOrganizationOptions] = useState([]);

    useEffect(() => {
        dispatch(getOrganizations());
    }, [dispatch]);

    useEffect(() => {
        setOrganizationOptions(organizations.map(org => ({
            value: org.id,
            label: org.name
        })));
    }, [organizations]);

    const handleInputChange = (e) => {
        const { name, value, type, checked } = e.target;
        setFormData(prevState => ({
            ...prevState,
            [name]: type === 'checkbox' ? checked : value
        }));
    };

    const handleOrganizationChange = (selectedOption, actionMeta) => {
        if (actionMeta.action === "create-option") {
            setFormData(prevState => ({
                ...prevState,
                organization: {
                    value: selectedOption.value,
                    label: selectedOption.label,
                    isNew: true
                }
            }));
        } else {
            setFormData(prevState => ({
                ...prevState,
                organization: selectedOption
            }));
        }
    };

    const validateForm = () => {
        let formErrors = {};
        if (!formData.firstName) formErrors.firstName = "First Name is required";
        if (!formData.lastName) formErrors.lastName = "Last Name is required";
        if (!formData.email) formErrors.email = "Email is required";
        else if (!/^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i.test(formData.email))
            formErrors.email = "Invalid email address";
        if (!formData.password) formErrors.password = "Password is required";
        else if (formData.password.length < 8)
            formErrors.password = "Password must be at least 8 characters long";
        if (formData.password !== formData.confirmPassword)
            formErrors.confirmPassword = "Passwords do not match";
        if (!formData.organization) formErrors.organization = "Organization is required";
        if (!formData.agreeTerms) formErrors.agreeTerms = "You must agree to the terms";

        setErrors(formErrors);
        return Object.keys(formErrors).length === 0;
    };

    const onFormSubmit = async (e) => {
        e.preventDefault();

        if (!validateForm()) {
            return;
        }

        try {
            let payload = {
                fName: formData.firstName,
                mName: formData.middleName,
                lName: formData.lastName,
                email: formData.email,
                password: formData.password,
            }

            if (formData.organization?.value && !formData.organization.isNew) {
                payload.organizationId = formData.organization.value;
            } else {
                payload.newOrganization = formData.organization?.label || "";
            }

            if (formData.middleName === "" || formData.middleName === undefined || formData.middleName === null) {
                delete payload.mName;
            }

            showProgressDialog('Please wait','Registering User ....');
            const result = await dispatch(registerUser(payload));
            closeProgressDialog();

            if (result.success) {
                dispatch(getOrganizations())
                showSuccessDialog(result.message)
                setFormData({
                    firstName: "",
                    middleName: "",
                    lastName: "",
                    email: "",
                    password: "",
                    confirmPassword: "",
                    organization: null,
                    agreeTerms: false
                });
            }
        } catch (error) {
            console.error("Registration error:", error);
        }
    };

    // Updated styles based on Apple and Google design principles
    const pageStyle = {
        minHeight: '100vh',
        display: 'flex',
        flexDirection: 'column',
        background: 'linear-gradient(120deg, #e3ecf7 0%, #f5f7fa 100%)',
        fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Oxygen, Ubuntu, Cantarell, sans-serif',
        animation: 'fadeIn 1.2s cubic-bezier(0.4,0,0.2,1)'
    };

    const contentStyle = {
        flex: 1,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '2rem',
        minHeight: '100vh',
        animation: 'fadeInUp 1.2s cubic-bezier(0.4,0,0.2,1)'
    };

    const cardStyle = {
        width: '100%',
        maxWidth: '1100px',
        minHeight: '650px',
        display: 'flex',
        boxShadow: '0 8px 32px rgba(0,0,0,0.16)',
        borderRadius: '32px',
        overflow: 'hidden',
        background: 'rgba(255,255,255,0.35)',
        backdropFilter: 'blur(18px)',
        WebkitBackdropFilter: 'blur(18px)',
        border: '1.5px solid rgba(255,255,255,0.25)'
    };

    const leftColumnStyle = {
        background: 'linear-gradient(135deg, #e3ecf7 60%, #b6cbe0 100%)',
        color: '#013e7d',
        padding: '2.5rem 1.5rem',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        textAlign: 'center',
        minWidth: 0,
        position: 'relative',
        overflow: 'hidden',
    };

    const rightColumnStyle = {
        background: 'rgba(255,255,255,0.98)',
        padding: '2.5rem 2.5rem',
        maxHeight: '90vh',
        overflowY: 'auto',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        borderRadius: '0 32px 32px 0',
        boxShadow: '0 2px 16px rgba(0,0,0,0.04)'
    };

    const imageStyle = {
        maxWidth: '220px',
        width: '100%',
        marginBottom: '2.5rem',
        filter: 'drop-shadow(0 4px 24px rgba(0,0,0,0.10))'
    };

    const footerStyle = {
        backgroundColor: 'transparent',
        padding: '1rem 0',
        textAlign: 'center',
        marginTop: 'auto',
    };

    const inputIconStyle = {
        position: 'absolute',
        left: '15px',
        top: '50%',
        transform: 'translateY(-50%)',
        color: '#6c757d',
        fontSize: '16px',
    };

    const inputStyle = {
        paddingLeft: '45px',
        height: '44px',
        borderRadius: '4px',
        border: '1px solid #ced4da',
        fontSize: '14px',
        backgroundColor: '#fff',
    };

    const buttonStyle = {
        backgroundColor: '#013e7d',
        border: 'none',
        borderRadius: '8px',
        padding: '10px 16px',
        fontSize: '1.08rem',
        fontWeight: 700,
        transition: 'all 0.2s cubic-bezier(0.4,0,0.2,1)',
        boxShadow: '0 2px 8px rgba(1,62,125,0.10)',
        height: '44px',
        width: '100%',
        letterSpacing: '-0.5px',
    };

    const dividerStyle = {
        height: '1px',
        backgroundColor: '#e9ecef',
        margin: '20px 0',
        width: '100%',
    };

    const selectStyles = {
        control: (provided) => ({
            ...provided,
            height: '44px',
            minHeight: '44px',
            paddingLeft: '35px',
            borderColor: '#ced4da',
            borderRadius: '4px',
            boxShadow: 'none',
            '&:hover': {
                borderColor: '#ced4da',
            }
        }),
        placeholder: (provided) => ({
            ...provided,
            color: '#6c757d',
            fontSize: '14px',
        }),
        valueContainer: (provided) => ({
            ...provided,
            padding: '0 8px',
        }),
        option: (provided, state) => ({
            ...provided,
            backgroundColor: state.isSelected ? '#013e7d' : state.isFocused ? '#e9ecef' : null,
            color: state.isSelected ? 'white' : '#013e7d',
            fontSize: '14px',
        }),
    };

    return (
        <div style={pageStyle}>
            <div style={contentStyle}>
                <Card style={cardStyle}>
                    <Row className="g-0">
                        <Col md={5} style={leftColumnStyle}>
                            <div style={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: '2.5rem' }}>
                                <img src={whitelog} alt="Meinhardt Logo" style={imageStyle} />
                            </div>
                            <h1 style={{
                                marginBottom: '0.7rem',
                                fontSize: '2.3rem',
                                color: '#013e7d',
                                fontWeight: 800,
                                letterSpacing: '-1.5px',
                                textShadow: '0 2px 16px rgba(0,0,0,0.10)'
                            }}>Join M-SPHERE</h1>
                            <p style={{
                                fontSize: '1.08rem',
                                marginBottom: '2rem',
                                lineHeight: '1.5',
                                maxWidth: '90%',
                                fontWeight: 400,
                                opacity: 0.98,
                                color: '#225080'
                            }}>
                                <span style={{ fontWeight: 600, fontSize: '1.13rem', color: '#013e7d' }}>Empowering Safety to the Frontline</span><br />
                                Register now to access our comprehensive HSSE management platform and enhance your organization's safety practices.
                            </p>
                        </Col>
                        <Col md={7} style={rightColumnStyle}>
                            <div style={{textAlign: 'center', marginBottom: '2rem'}}>
                                <h4 style={{
                                    fontSize: '1.5rem',
                                    color: '#013e7d',
                                    marginBottom: '0.5rem',
                                    textAlign: 'center',
                                    fontWeight: 800,
                                    letterSpacing: '-0.5px'
                                }}>M-SPHERE | HSE SYSTEM</h4>
                                <div style={{
                                    height: '3px',
                                    width: '40px',
                                    background: '#013e7d',
                                    margin: '0.7rem auto 0',
                                    borderRadius: 2
                                }}></div>
                            </div>
                            <h4 style={{
                                fontSize: '1.15rem',
                                color: '#333333',
                                marginBottom: '1.5rem',
                                textAlign: 'center',
                                fontWeight: 400
                            }}>Register a new membership</h4>

                            <Form onSubmit={onFormSubmit}>
                                <Row>
                                    <Col md={6}>
                                        <FormGroup style={{ marginBottom: '1.25rem', position: 'relative' }}>
                                            <Label style={{
                                                fontSize: '0.875rem',
                                                marginBottom: '8px',
                                                fontWeight: '400',
                                                color: '#333333'
                                            }}>First Name</Label>
                                            <div style={{ position: 'relative' }}>
                                                <FiUser style={inputIconStyle} />
                                                <Input
                                                    type="text"
                                                    name="firstName"
                                                    value={formData.firstName}
                                                    onChange={handleInputChange}
                                                    placeholder="First Name"
                                                    style={inputStyle}
                                                    className="form-control"
                                                />
                                            </div>
                                            {errors.firstName && <span style={{
                                                color: '#dc3545',
                                                fontSize: '0.8rem',
                                                marginTop: '5px',
                                                display: 'block'
                                            }}>{errors.firstName}</span>}
                                        </FormGroup>
                                    </Col>
                                    <Col md={6}>
                                        <FormGroup style={{ marginBottom: '1.25rem', position: 'relative' }}>
                                            <Label style={{
                                                fontSize: '0.875rem',
                                                marginBottom: '8px',
                                                fontWeight: '400',
                                                color: '#333333'
                                            }}>Last Name</Label>
                                            <div style={{ position: 'relative' }}>
                                                <FiUser style={inputIconStyle} />
                                                <Input
                                                    type="text"
                                                    name="lastName"
                                                    value={formData.lastName}
                                                    onChange={handleInputChange}
                                                    placeholder="Last Name"
                                                    style={inputStyle}
                                                    className="form-control"
                                                />
                                            </div>
                                            {errors.lastName && <span style={{
                                                color: '#dc3545',
                                                fontSize: '0.8rem',
                                                marginTop: '5px',
                                                display: 'block'
                                            }}>{errors.lastName}</span>}
                                        </FormGroup>
                                    </Col>
                                </Row>

                                <FormGroup style={{ marginBottom: '1.25rem', position: 'relative' }}>
                                    <Label style={{
                                        fontSize: '0.875rem',
                                        marginBottom: '8px',
                                        fontWeight: '400',
                                        color: '#333333'
                                    }}>Middle Name (Optional)</Label>
                                    <div style={{ position: 'relative' }}>
                                        <FiUser style={inputIconStyle} />
                                        <Input
                                            type="text"
                                            name="middleName"
                                            value={formData.middleName}
                                            onChange={handleInputChange}
                                            placeholder="Middle Name"
                                            style={inputStyle}
                                            className="form-control"
                                        />
                                    </div>
                                </FormGroup>

                                <FormGroup style={{ marginBottom: '1.25rem', position: 'relative' }}>
                                    <Label style={{
                                        fontSize: '0.875rem',
                                        marginBottom: '8px',
                                        fontWeight: '400',
                                        color: '#333333'
                                    }}>Email</Label>
                                    <div style={{ position: 'relative' }}>
                                        <FiMail style={inputIconStyle} />
                                        <Input
                                            type="email"
                                            name="email"
                                            value={formData.email}
                                            onChange={handleInputChange}
                                            placeholder="Email"
                                            style={inputStyle}
                                            className="form-control"
                                        />
                                    </div>
                                    {errors.email && <span style={{
                                        color: '#dc3545',
                                        fontSize: '0.8rem',
                                        marginTop: '5px',
                                        display: 'block'
                                    }}>{errors.email}</span>}
                                </FormGroup>

                                <Row>
                                    <Col md={6}>
                                        <FormGroup style={{ marginBottom: '1.25rem', position: 'relative' }}>
                                            <Label style={{
                                                fontSize: '0.875rem',
                                                marginBottom: '8px',
                                                fontWeight: '400',
                                                color: '#333333'
                                            }}>Password</Label>
                                            <div style={{ position: 'relative' }}>
                                                <FiLock style={inputIconStyle} />
                                                <Input
                                                    type="password"
                                                    name="password"
                                                    value={formData.password}
                                                    onChange={handleInputChange}
                                                    placeholder="Password"
                                                    style={inputStyle}
                                                    className="form-control"
                                                />
                                            </div>
                                            {errors.password && <span style={{
                                                color: '#dc3545',
                                                fontSize: '0.8rem',
                                                marginTop: '5px',
                                                display: 'block'
                                            }}>{errors.password}</span>}
                                        </FormGroup>
                                    </Col>
                                    <Col md={6}>
                                        <FormGroup style={{ marginBottom: '1.25rem', position: 'relative' }}>
                                            <Label style={{
                                                fontSize: '0.875rem',
                                                marginBottom: '8px',
                                                fontWeight: '400',
                                                color: '#333333'
                                            }}>Confirm Password</Label>
                                            <div style={{ position: 'relative' }}>
                                                <FiLock style={inputIconStyle} />
                                                <Input
                                                    type="password"
                                                    name="confirmPassword"
                                                    value={formData.confirmPassword}
                                                    onChange={handleInputChange}
                                                    placeholder="Confirm password"
                                                    style={inputStyle}
                                                    className="form-control"
                                                />
                                            </div>
                                            {errors.confirmPassword && <span style={{
                                                color: '#dc3545',
                                                fontSize: '0.8rem',
                                                marginTop: '5px',
                                                display: 'block'
                                            }}>{errors.confirmPassword}</span>}
                                        </FormGroup>
                                    </Col>
                                </Row>

                                <FormGroup style={{ marginBottom: '1.25rem', position: 'relative' }}>
                                    <Label style={{
                                        fontSize: '0.875rem',
                                        marginBottom: '8px',
                                        fontWeight: '400',
                                        color: '#333333'
                                    }}>Organization</Label>
                                    <div style={{ position: 'relative' }}>
                                        <FiBriefcase style={{
                                            position: 'absolute',
                                            left: '15px',
                                            top: '13px',
                                            zIndex: 10,
                                            color: '#6c757d',
                                            fontSize: '16px',
                                        }} />
                                        <Select
                                            isClearable
                                            options={organizationOptions}
                                            value={formData.organization}
                                            onChange={handleOrganizationChange}
                                            placeholder="Select or type to create new"
                                            formatCreateLabel={(inputValue) => `Create "${inputValue}"`}
                                            styles={selectStyles}
                                        />
                                    </div>
                                    {errors.organization && <span style={{
                                        color: '#dc3545',
                                        fontSize: '0.8rem',
                                        marginTop: '5px',
                                        display: 'block'
                                    }}>{errors.organization}</span>}
                                </FormGroup>

                                <FormGroup className="mb-3" style={{ marginBottom: '1.25rem' }}>
                                    <div className="d-flex align-items-center">
                                        <div style={{ position: 'relative', marginRight: '8px' }}>
                                            <Input
                                                type="checkbox"
                                                id="agreeTerms"
                                                name="agreeTerms"
                                                checked={formData.agreeTerms}
                                                onChange={handleInputChange}
                                                className="form-check-input"
                                                style={{ marginTop: '0' }}
                                            />
                                        </div>
                                        <Label check for="agreeTerms" className="mb-0" style={{
                                            fontSize: '0.875rem',
                                            fontWeight: '400',
                                            color: '#333333'
                                        }}>
                                            I agree to the <Link to="/terms" style={{ color: '#013e7d', textDecoration: 'none' }}>terms</Link>
                                        </Label>
                                    </div>
                                    {errors.agreeTerms && <span style={{
                                        color: '#dc3545',
                                        fontSize: '0.8rem',
                                        marginTop: '5px',
                                        display: 'block'
                                    }}>{errors.agreeTerms}</span>}
                                </FormGroup>

                                <Button
                                    color="primary"
                                    block
                                    type="submit"
                                    style={buttonStyle}
                                    className="btn-register-hover"
                                    onMouseOver={e => e.currentTarget.style.background = '#225080'}
                                    onMouseOut={e => e.currentTarget.style.background = '#013e7d'}
                                >
                                    Register
                                </Button>
                                <style>
                                    {`
                                        .btn-register-hover:hover {
                                            background: #225080 !important;
                                        }
                                    `}
                                </style>
                            </Form>

                            <div style={dividerStyle}></div>

                            <div style={{
                                textAlign: 'center',
                                marginTop: '1rem'
                            }}>
                                <p style={{
                                    color: '#6c757d',
                                    margin: 0,
                                    fontSize: '0.98rem'
                                }}>
                                    Already have an account?{" "}
                                    <Link
                                        to="/login"
                                        style={{
                                            fontWeight: 600,
                                            color: '#013e7d',
                                            textDecoration: 'none'
                                        }}
                                    >
                                        Login
                                    </Link>
                                </p>
                            </div>
                            {/* Support info below Login link */}
                            <div style={{ textAlign: 'center', marginTop: 24 }}>
                                <p style={{ margin: '0 0 0.5rem', fontSize: '0.93rem', color: '#013e7d', fontWeight: 500 }}>
                                    © {new Date().getFullYear()} HSSE. Crafted by M-SPHERE
                                </p>
                                <p style={{ color: '#013e7d', textDecoration: 'none', fontSize: '0.93rem', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px' }}>
                                    <MdEmail style={{ fontSize: '1rem' }}/> hsse.support@meinhardtepcm.com
                                </p>
                            </div>
                        </Col>
                    </Row>
                </Card>
            </div>
            <style>{`
            @keyframes fadeIn {
              from { opacity: 0; }
              to { opacity: 1; }
            }
            @keyframes fadeInUp {
              from { opacity: 0; transform: translateY(40px); }
              to { opacity: 1; transform: translateY(0); }
            }
            `}</style>
        </div>
    );
};

export default withRouter(Register);